import {
	type PortalOrderListPage,
	type PortalOrderRow,
	type PortalOrderTab,
	saveFile,
} from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import { formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { ORDER_STATUSES } from "@khazzin/ui/patterns/orderStatus";
import {
	EmptyState,
	type FilterDefinition,
	FilterDrawer,
	PageHeader,
} from "@khazzin/ui/patterns/page";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import { Button, Tabs } from "@khazzin/ui/primitives";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useApi } from "../../api";
import { SearchInput } from "../../app/SearchInput";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { type Level, levelOf } from "../../app/shell/sections";
import { useServiceOff } from "../../app/shell/serviceSwitches";
import { EditOrderLink, WithdrawButton } from "./actions";
import { ContactChip, OrderStatusCell } from "./chips";
import { DispatchesTab } from "./DispatchesTab";
import { ExternalImportTab } from "./ExternalImportTab";
import { MissingStockTab } from "./MissingStockTab";
import { PickupsTab } from "./PickupsTab";

/**
 * The Store Portal's Orders (EP10-S03, DA-10-03): Existing, Website and
 * Archived, with search, the filter drawer, Export Orders, and CR-019's
 * columns — the read-only Customer Contact chip, Cashed Out and Awaiting
 * confirmation among them.
 *
 * **The URL is the state**: the tab, the search, the filters and the page
 * size, so a filtered tab is a link and a reload lands on the same rows.
 *
 * **The store is the query key's**, after `portal`: a store switch drops every
 * cached page (`scope.tsx`), and a page of the old store can never answer the
 * new one.
 *
 * **New orders arrive within 5 s** through the live stream (EP10-S22): an
 * order's hint invalidates `["portal", storeId, "orders"]` (the shell's
 * `storeTopics`), and a stream that is down falls back to a 60 s re-read.
 *
 * The row's Actions are View, and — while `pending`, at Orders = write — Edit
 * and the circle withdraw (EP10-S05). New Order is its own section; the header
 * links to it. The Missing Stock and External Import tabs (EP10-S04) are the
 * External Import section's, shown beside the lists when the grid shows it.
 */

export const PORTAL_ORDER_TABS: readonly PortalOrderTab[] = ["existing", "website", "archived"];

/** The drawer's parameters, which the URL carries under the same names. */
export const PORTAL_ORDER_FILTERS = [
	"status",
	"type",
	"urgency",
	"returnStatus",
	"customerContactStatus",
	"createdFrom",
	"createdTo",
] as const;

const PAGE_SIZES = [25, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

/** The tab a URL names; anything else is Existing. */
export function tabOf(raw: string | null): PortalOrderTab {
	return PORTAL_ORDER_TABS.find((tab) => tab === raw) ?? "existing";
}

/**
 * EP10-S04's two tabs, beside the lists: the store's own queues rather than a
 * list of orders, shown only when the External Import section is.
 */
export const IMPORT_TABS = ["missing_stock", "external_import"] as const;
export type ImportTab = (typeof IMPORT_TABS)[number];

/** The import tab a URL names, when the account may see it; otherwise null. */
export function importTabOf(raw: string | null, level: Level): ImportTab | null {
	return level === "none" ? null : (IMPORT_TABS.find((tab) => tab === raw) ?? null);
}

/** The drawer's values out of the URL. */
export function filtersOf(params: URLSearchParams): Record<string, string> {
	const out: Record<string, string> = {};
	for (const key of PORTAL_ORDER_FILTERS) {
		const value = params.get(key);
		if (value !== null && value !== "") {
			out[key] = value;
		}
	}
	return out;
}

/** The list's cache key: the store first, so a switch can never read another store's page. */
export function portalOrdersKey(
	storeId: string,
	tab: PortalOrderTab,
	q: string,
	filters: Record<string, string>,
) {
	return ["portal", storeId, "orders", tab, q, filters] as const;
}

export function OrdersPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const navigate = useNavigate();
	const { store, context } = useStoreScope();
	const [params, setParams] = useSearchParams();
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [exportFailed, setExportFailed] = useState(false);

	const level = levelOf(store, "orders");
	const storeId = store?.storeId ?? "";
	const tab = tabOf(params.get("tab"));
	const importLevel = levelOf(store, "external_import");
	const importTab = importTabOf(params.get("tab"), importLevel);
	// EP03-S21: Pickup requests, a tab of Orders beside the lists.
	const pickupsTab = params.get("tab") === "pickups";
	const serviceOff = useServiceOff(storeId);
	// ADJ-R01-12: the store's bulk loads (EP07-S23's My Dispatches).
	const dispatchesTab = params.get("tab") === "dispatches";
	const q = params.get("q") ?? "";
	const filters = useMemo(() => filtersOf(params), [params]);
	const pageSize =
		PAGE_SIZES.find((size) => String(size) === params.get("limit")) ?? DEFAULT_PAGE_SIZE;
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";

	const page = useCursorPagination<
		PortalOrderRow,
		PortalOrderListPage & { nextCursor: string | null }
	>(
		async (cursor, size, signal) => {
			const answer = await api.listPortalOrders({
				tab,
				q,
				filters,
				limit: size,
				...(cursor === null ? {} : { cursor }),
				signal,
			});
			return { ...answer, nextCursor: answer.nextCursor ?? null };
		},
		{
			queryKey: portalOrdersKey(storeId, tab, q, filters),
			limit: pageSize,
			maxLimit: 200,
			enabled:
				level !== "none" && storeId !== "" && importTab === null && !pickupsTab && !dispatchesTab,
		},
	);

	const update = (next: Record<string, string | null>) => {
		const merged = new URLSearchParams(params);
		for (const [key, value] of Object.entries(next)) {
			if (value === null || value === "") {
				merged.delete(key);
			} else {
				merged.set(key, value);
			}
		}
		setParams(merged, { replace: true });
		page.reset();
	};

	const definitions = useMemo(() => filterDefinitions(t), [t]);
	const filtered = q !== "" || Object.keys(filters).length > 0;

	if (level === "none") {
		return <NotFound />;
	}

	async function exportOrders() {
		setExporting(true);
		setExportFailed(false);
		try {
			saveFile(
				await api.downloadPortalOrders(
					new URLSearchParams({ tab, ...(q === "" ? {} : { q }), ...filters }),
				),
			);
		} catch {
			setExportFailed(true);
		} finally {
			setExporting(false);
		}
	}

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.orders.title")}
				subtitle={t("portal.orders.subtitle")}
				{...(levelOf(store, "new_order") === "write"
					? {
							primaryAction: (
								<Link
									to="/new-order"
									className="inline-flex min-h-12 items-center rounded-md border border-brand bg-brand px-3 text-[13px] font-bold text-fg-on-brand hover:bg-brand-hover md:min-h-8"
								>
									{t("portal.orders.actions.new")}
								</Link>
							),
						}
					: {})}
				secondaryActions={
					<Button
						variant="secondary"
						size="sm"
						disabled={exporting}
						onClick={() => void exportOrders()}
					>
						{t("portal.orders.export.action")}
					</Button>
				}
			/>
			{exportFailed && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{t("portal.orders.export.failed")}
				</p>
			)}

			<Tabs
				aria-label={t("portal.orders.tabs.label")}
				value={pickupsTab ? "pickups" : dispatchesTab ? "dispatches" : (importTab ?? tab)}
				onValueChange={(value) => update({ tab: value === "existing" ? null : value })}
				tabs={[
					...PORTAL_ORDER_TABS,
					...(serviceOff("PICKUP") ? [] : ["pickups"]),
					"dispatches",
					...(importLevel === "none" ? [] : IMPORT_TABS),
				].map((value) => ({
					value,
					label: t(`portal.orders.tabs.${value}`),
				}))}
			/>

			{importTab === "missing_stock" && (
				<MissingStockTab storeId={storeId} level={importLevel} timeZone={timeZone} />
			)}
			{importTab === "external_import" && (
				<ExternalImportTab storeId={storeId} level={importLevel} timeZone={timeZone} />
			)}
			{pickupsTab && <PickupsTab storeId={storeId} level={level} timeZone={timeZone} />}
			{dispatchesTab && <DispatchesTab storeId={storeId} timeZone={timeZone} />}
			{importTab === null && !pickupsTab && !dispatchesTab && (
				<>
					<div className="flex flex-wrap items-center gap-2">
						<SearchInput
							value={q}
							onCommit={(next) => update({ q: next })}
							aria-label={t("portal.orders.search.label")}
							placeholder={t("portal.orders.search.placeholder")}
							className="max-w-72"
						/>
						<Button
							variant="secondary"
							size="sm"
							className="min-h-12 md:min-h-0"
							onClick={() => setFiltersOpen(true)}
						>
							{t("portal.orders.filters.action")}
							{Object.keys(filters).length > 0 && (
								<span className="ms-1 rounded-full bg-brand-subtle px-1.5 text-[11px] [font-variant-numeric:tabular-nums]">
									{Object.keys(filters).length}
								</span>
							)}
						</Button>
						<Button
							variant="ghost"
							size="sm"
							disabled={!filtered}
							onClick={() =>
								update({
									q: null,
									...Object.fromEntries(PORTAL_ORDER_FILTERS.map((key) => [key, null])),
								})
							}
						>
							{t("ui.actions.clear")}
						</Button>
					</div>

					<DataTable<PortalOrderRow>
						screenKey="portal.orders"
						caption={t("portal.orders.title")}
						t={t}
						columns={portalOrderColumns({
							t,
							locale,
							timeZone,
							writable: level === "write",
							storeId,
						})}
						rowKey={(row) => row.orderId}
						page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
						status={page.status}
						error={page.error}
						onRetry={page.refetch}
						onRowClick={(row) => void navigate(`/orders/${encodeURIComponent(row.orderId)}`)}
						collapse="cards"
						pagination={{
							showingFrom: page.showingFrom,
							showingTo: page.showingTo,
							total: page.page?.total,
							hasNext: page.hasNext,
							hasPrevious: page.hasPrevious,
							onNext: page.next,
							onPrevious: page.previous,
							page: page.pageNumber,
							onFirst: page.reset,
							onPage: page.goTo,
							pageSize,
							pageSizes: PAGE_SIZES,
							onPageSize: (size) =>
								update({ limit: size === DEFAULT_PAGE_SIZE ? null : String(size) }),
						}}
						empty={
							<EmptyState
								title={t(
									filtered ? "portal.orders.empty.filteredTitle" : "portal.orders.empty.title",
								)}
								body={t(filtered ? "portal.orders.empty.filteredBody" : "portal.orders.empty.body")}
							/>
						}
					/>

					<FilterDrawer
						open={filtersOpen}
						onOpenChange={setFiltersOpen}
						definitions={definitions}
						values={filters}
						onApply={(values) => {
							update(
								Object.fromEntries(PORTAL_ORDER_FILTERS.map((key) => [key, values[key] ?? null])),
							);
							setFiltersOpen(false);
						}}
						t={t}
					/>
				</>
			)}
		</div>
	);
}

const EXCEPTIONS = new Set([
	"delivery_failed",
	"rescheduled",
	"partially_delivered",
	"returning",
	"returned",
	"lost",
]);
const ORDER_TYPES: readonly PortalOrderRow["type"][] = [
	"delivery",
	"pickup",
	"exchange",
	"pickup_design",
];

function filterDefinitions(t: Translate): FilterDefinition[] {
	const options = (prefix: string, values: readonly string[]) =>
		values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));
	const firstException = ORDER_STATUSES.find((status) => EXCEPTIONS.has(status)) ?? "";
	return [
		{
			id: "status",
			label: t("portal.orders.filters.status"),
			kind: "multiselect",
			options: options("orders.status", ORDER_STATUSES),
			optionHeadings: { [firstException]: t("portal.orders.filters.exceptions") },
		},
		{
			id: "type",
			label: t("portal.orders.filters.type"),
			kind: "multiselect",
			options: options("orders.type", ORDER_TYPES),
		},
		{
			id: "urgency",
			label: t("portal.orders.filters.urgency"),
			kind: "select",
			options: options("orders.urgency", ["normal", "urgent"]),
		},
		{
			id: "returnStatus",
			label: t("portal.orders.filters.returnStatus"),
			kind: "multiselect",
			options: options("orders.returnStatus", ["none", "requested", "in_progress", "completed"]),
		},
		{
			id: "customerContactStatus",
			label: t("portal.orders.filters.contact"),
			kind: "multiselect",
			options: options("orders.contactStatus", [
				"not_contacted",
				"auto_texted",
				"texted",
				"called_no_answer",
				"confirmed",
				"reschedule_requested",
				"cancel_requested",
				"wrong_number",
			]),
		},
		{ id: "createdFrom", label: t("portal.orders.filters.createdFrom"), kind: "date" },
		{ id: "createdTo", label: t("portal.orders.filters.createdTo"), kind: "date" },
	];
}

/** A value the server left empty reads as a dash, never as a blank that looks like "none". */
function dash(value: string | null | undefined): string {
	return value === null || value === undefined || value === "" ? "—" : value;
}

/** CR-019's columns, in the live app's order. */
export function portalOrderColumns({
	t,
	locale,
	timeZone,
	writable = false,
	storeId = "",
}: {
	t: Translate;
	locale: Locale;
	timeZone: string;
	/** Orders at write: a `pending` row carries Edit and the circle (EP10-S05). */
	writable?: boolean;
	storeId?: string;
}): Column<PortalOrderRow>[] {
	const at = (value: string | null | undefined) =>
		value === null || value === undefined ? "—" : formatDateTime(value, { locale, timeZone });
	return [
		{
			id: "orderNo",
			headerKey: "portal.orders.columns.orderNo",
			mono: true,
			card: "wide",
			cell: (row) => (
				<Link
					to={`/orders/${encodeURIComponent(row.orderId)}`}
					className="font-mono text-[12.5px] font-semibold text-brand-ink underline-offset-2 hover:underline"
				>
					{row.orderNo}
				</Link>
			),
		},
		{
			id: "customer",
			headerKey: "portal.orders.columns.customer",
			card: "wide",
			cell: (row) => row.customerName,
		},
		{
			id: "status",
			headerKey: "portal.orders.columns.status",
			cell: (row) => <OrderStatusCell row={row} />,
		},
		{
			id: "contact",
			headerKey: "portal.orders.columns.contact",
			hideable: true,
			cell: (row) => <ContactChip status={row.customerContactStatus} />,
		},
		{
			id: "returnStatus",
			headerKey: "portal.orders.columns.returnStatus",
			hideable: true,
			cell: (row) =>
				row.returnStatus === "none" ? "—" : t(`orders.returnStatus.${row.returnStatus}`),
		},
		{
			id: "urgency",
			headerKey: "portal.orders.columns.urgency",
			hideable: true,
			cell: (row) => t(`orders.urgency.${row.urgency}`),
		},
		{
			id: "type",
			headerKey: "portal.orders.columns.type",
			hideable: true,
			cell: (row) => t(`orders.type.${row.type}`),
		},
		{
			id: "province",
			headerKey: "portal.orders.columns.province",
			hideable: true,
			cell: (row) => dash(row.provinceName),
		},
		{
			id: "city",
			headerKey: "portal.orders.columns.city",
			hideable: true,
			cell: (row) => dash(row.cityName),
		},
		{
			id: "deliveryCompany",
			headerKey: "portal.orders.columns.deliveryCompany",
			hideable: true,
			cell: (row) => dash(row.deliveryCompanyName),
		},
		{
			id: "total",
			headerKey: "portal.orders.columns.total",
			align: "end",
			cell: (row) => formatMoney({ amountMinor: row.totalMinor, currency: row.currency }, locale),
		},
		{
			id: "created",
			headerKey: "portal.orders.columns.created",
			hideable: true,
			cell: (row) => at(row.createdAt),
		},
		{
			id: "fulfilled",
			headerKey: "portal.orders.columns.fulfilled",
			hideable: true,
			cell: (row) => at(row.fulfilledAt),
		},
		{
			id: "actions",
			headerKey: "portal.orders.columns.actions",
			// ADJ-M03-4: on a phone's card too, or Edit and Withdraw are out of
			// reach; `data-row-stop` so a press here does not also open the order.
			card: "wide",
			cell: (row) => (
				<div data-row-stop="1" className="flex items-center gap-3">
					<Link
						to={`/orders/${encodeURIComponent(row.orderId)}`}
						className="text-[12.5px] font-semibold text-brand-ink underline-offset-2 hover:underline"
					>
						{t("portal.orders.actions.view")}
					</Link>
					{writable && row.status === "pending" && (
						<>
							<EditOrderLink orderId={row.orderId} />
							<WithdrawButton orderId={row.orderId} orderNo={row.orderNo} storeId={storeId} />
						</>
					)}
				</div>
			),
		},
	];
}

export default OrdersPage;
